import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import * as z from 'zod'

export function useUserSchema() {
  const intl = useIntl()

  const userSchema = z.object({
    // Account Information (Required)
    email: z
      .string()
      .min(1, intl.formatMessage({ id: 'validation.required', defaultMessage: 'Required' }))
      .email(intl.formatMessage({ id: 'validation.invalid_email_address', defaultMessage: 'Invalid email address' })),
    phoneNumber: z
      .string()
      .min(1, intl.formatMessage({ id: 'validation.required', defaultMessage: 'Required' }))
      .regex(
        /^\+[1-9]\d{1,14}$/,
        intl.formatMessage({
          id: 'validation.invalid_phone_number_format',
          defaultMessage: 'Invalid phone number format (E.164)',
        }),
      ),
    locale: z.string().min(1, intl.formatMessage({ id: 'validation.required', defaultMessage: 'Required' })),
    role: z.string().min(1, intl.formatMessage({ id: 'validation.required', defaultMessage: 'Required' })),

    // Personal Information (Required)
    lastname: z
      .string()
      .min(1, intl.formatMessage({ id: 'validation.required', defaultMessage: 'Required' }))
      .regex(
        /^[\p{L}\s\-']+$/u,
        intl.formatMessage({
          id: 'validation.lastname_only_letters',
          defaultMessage: 'Lastname must contain only letters',
        }),
      ),
    birthdate: z
      .string()
      .min(1, intl.formatMessage({ id: 'validation.required', defaultMessage: 'Required' }))
      .regex(
        /^\d{4}-\d{2}-\d{2}$/,
        intl.formatMessage({ id: 'validation.invalid_date', defaultMessage: 'Invalid date format (YYYY-MM-DD)' }),
      ),

    // Personal Information (Optional)
    firstname: z
      .string()
      .regex(
        /^[\p{L}\s\-']*$/u,
        intl.formatMessage({
          id: 'validation.firstname_only_letters',
          defaultMessage: 'Firstname must contain only letters',
        }),
      )
      .optional()
      .or(z.literal('')),
    gender: z
      .string()
      .refine((val) => !val || ['0', '1', '2', '3'].includes(val), {
        message: intl.formatMessage({ id: 'validation.invalid_gender', defaultMessage: 'Invalid gender' }),
      })
      .optional()
      .or(z.literal('')),
    nationality: z
      .string()
      .regex(
        /^[a-z]{2}$/,
        intl.formatMessage({
          id: 'validation.invalid_country_code',
          defaultMessage: 'Must be 2-letter country code',
        }),
      )
      .optional()
      .or(z.literal('')),

    // Address Information (Optional)
    country: z
      .string()
      .regex(
        /^[a-z]{2}$/,
        intl.formatMessage({
          id: 'validation.invalid_country_code',
          defaultMessage: 'Must be 2-letter country code',
        }),
      )
      .optional()
      .or(z.literal('')),
    street: z.string().optional().or(z.literal('')),
    city: z.string().optional().or(z.literal('')),
    postCode: z.string().optional().or(z.literal('')),

    // Profile Picture (Optional)
    picture: z
      .string()
      .url(intl.formatMessage({ id: 'validation.invalid_url', defaultMessage: 'Invalid URL' }))
      .optional()
      .or(z.literal('')),
  })

  return useMemo(() => userSchema, [userSchema])
}

export function useSignUpSchema() {
  const intl = useIntl()

  const signUpSchema = z
    .object({
      email: z
        .string()
        .min(1, intl.formatMessage({ id: 'validation.required', defaultMessage: 'Required' }))
        .email(intl.formatMessage({ id: 'validation.invalid_email_address', defaultMessage: 'Invalid email address' })),
      phoneNumber: z
        .string()
        .min(1, intl.formatMessage({ id: 'validation.required', defaultMessage: 'Required' }))
        .regex(
          /^\+[1-9]\d{1,14}$/,
          intl.formatMessage({
            id: 'validation.invalid_phone_number_format',
            defaultMessage: 'Invalid phone number format (E.164)',
          }),
        ),
      firstname: z
        .string()
        .min(
          3,
          intl.formatMessage({ id: 'validation.min_3_characters', defaultMessage: 'Must be at least 3 characters' }),
        )
        .regex(
          /^[\p{L}\s\-']+$/u,
          intl.formatMessage({
            id: 'validation.firstname_only_letters',
            defaultMessage: 'Firstname must contain only letters',
          }),
        ),
      lastname: z
        .string()
        .min(
          3,
          intl.formatMessage({ id: 'validation.min_3_characters', defaultMessage: 'Must be at least 3 characters' }),
        )
        .regex(
          /^[\p{L}\s\-']+$/u,
          intl.formatMessage({
            id: 'validation.lastname_only_letters',
            defaultMessage: 'Lastname must contain only letters',
          }),
        ),
      birthdate: z
        .string()
        .min(1, intl.formatMessage({ id: 'validation.required', defaultMessage: 'Required' }))
        .regex(
          /^\d{4}-\d{2}-\d{2}$/,
          intl.formatMessage({ id: 'validation.invalid_date', defaultMessage: 'Invalid date format (YYYY-MM-DD)' }),
        ),
      gender: z
        .string()
        .min(1, intl.formatMessage({ id: 'validation.required', defaultMessage: 'Required' }))
        .refine((val) => ['0', '1', '2', '3'].includes(val), {
          message: intl.formatMessage({ id: 'validation.invalid_gender', defaultMessage: 'Invalid gender' }),
        }),
      locale: z.string().optional(),
      role: z.string().optional(),
      // Organizer-specific fields
      cooperationAgreement: z.boolean().optional(),
      dataProcessingAgreement: z.boolean().optional(),
      turnstileToken: z.string().min(
        1,
        intl.formatMessage({
          id: 'validation.turnstile_required',
          defaultMessage: 'Please complete the verification challenge',
        }),
      ),
    })
    .superRefine((data, ctx) => {
      if (data.role === 'organizer') {
        if (!data.cooperationAgreement) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['cooperationAgreement'],
            message: intl.formatMessage({
              id: 'validation.must_accept_cooperation',
              defaultMessage: 'You must accept the Cooperation Agreement',
            }),
          })
        }
        if (!data.dataProcessingAgreement) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dataProcessingAgreement'],
            message: intl.formatMessage({
              id: 'validation.must_accept_data_processing',
              defaultMessage: 'You must accept the Data Processing Agreement',
            }),
          })
        }
      }
    })

  return useMemo(() => signUpSchema, [signUpSchema])
}
